/**
 * Product service for Custom Backend operations
 * Handles all product-related API calls
 */

import { BaseCustomService } from "../services/base";
import {
  mapGetCollectionToResponse,
  mapGetProductsToResponse,
  mapGetProductToResponse,
} from "./mappers";
import {
  IProductQueryReqest,
  ISingleProductRequest,
  IOptionsRequest,
  IResponse,
  ISingleCollectionRequest,
} from "../../../models";
import { formatResponse } from "../../../core/utils";
import { ICustomConfig } from "../interface";
import { Logger } from "../../../logger";

export class ProductService extends BaseCustomService {
  constructor(baseUrl: string, config: ICustomConfig, logger?: Logger) {
    super(baseUrl, config, logger);
  }

  /**
   * Get products based on query parameters
   */
  async getProducts(
    params: IProductQueryReqest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Set defaults
      const DEFAULT_FIRST = 20;
      const MAX_FIRST = 100;

      const first =
        typeof params.first === "number" && params.first > 0
          ? Math.min(params.first, MAX_FIRST)
          : DEFAULT_FIRST;

      const requestParams = {
        ...params,
        first,
      };

      // Execute API request
      const result = await this.request(
        "/products/list",
        "POST",
        requestParams,
        options
      );

      // Handle missing / empty data
      if (!result || !result.products) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No products found.",
          data: [],
          meta: { pagination: undefined },
        });
      }

      const formattedResult = mapGetProductsToResponse(result);
      // Normal success path
      return formatResponse({
        success: true,
        message: "Products retrieved successfully.",
        data: formattedResult.data,
        meta: {
          ...formattedResult?.meta,
          pagination: result.pagination,
        },
      });
    } catch (error: any) {
      // Log the error safely
      this.logger.error("Error in getProducts", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a product by ID or handle
   */
  async getProduct(
    params: ISingleProductRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Validation & Input Processing
      const { id, handle } = params || {};

      if (!id && !handle) {
        return formatResponse({
          success: false,
          message: "No product ID or handle provided.",
        });
      }

      const payload: any = {};
      if (handle) {
        payload.productHandle = handle;
      } else if (id) {
        payload.productId = id.toString().includes("gid://")
          ? id.toString()
          : `gid://shopify/Product/${id.toString()}`;
      }

      // Execute API request
      const result = await this.request(
        "/product/details",
        "POST",
        payload,
        options
      );

      // Handle missing / empty data
      if (!result || !result.product) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No product found.",
          data: null,
          meta: undefined,
        });
      }

      // Normal success path
      return formatResponse({
        success: true,
        message: "Product retrieved successfully.",
        data: mapGetProductToResponse(result),
        meta: undefined,
      });
    } catch (error: any) {
      // Log the error safely
      this.logger.error("Error in getProduct", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get collection by handle
   */
  async getCollection(
    params: ISingleCollectionRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      const { handle, id, ...otherParams } = params;

      if (!handle) {
        return formatResponse({
          success: false,
          message: "Missing required variable: 'handle'",
        });
      }

      // Build query parameters
      const queryParams: Record<string, any> = {
        available: true,
        ...otherParams,
      };

      // Execute API request
      const result = await this.request(
        `/collections/handle/${handle}`,
        "GET",
        undefined,
        { params: queryParams, ...(options || {}) }
      );

      // Handle missing / empty data
      if (!result) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No collection found.",
          data: { id: "", title: "", handle: "", products: [] },
          meta: undefined,
        });
      }
      // Normal success path
      return formatResponse({
        success: true,
        message: "Collection retrieved successfully.",
        data: mapGetCollectionToResponse(result),
        meta: {
          pagination: result.productPagination,
        },
      });
    } catch (error: any) {
      // Log the error safely
      this.logger.error("Error in getCollection", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
